import { useCallback, useMemo } from 'react';

import { isCryptoAccount } from 'utils/accounts/isCryptoAccount';

import { AccountStatus } from 'api';
import { useVirtualAccountsContext } from 'contexts';

import type { VirtualAccount } from 'models/virtualAccount';

export type FindAccountByAccountNumber = (
  accountNumber: string | undefined
) => VirtualAccount | undefined;

interface UseVirtualAccountsOptions {
  filterUnownedAccounts?: boolean;
  filterTerminatedAccounts?: boolean;
  filterSuspendedAccounts?: boolean;
  filterCryptoAccounts?: boolean;
}

export const useVirtualAccounts = ({
  filterUnownedAccounts = false,
  filterTerminatedAccounts = false,
  filterSuspendedAccounts = false,
  filterCryptoAccounts = false
}: UseVirtualAccountsOptions = {}) => {
  const { accounts, isFetching, isSuccess, isLoading } =
    useVirtualAccountsContext();

  const filteredAccounts = useMemo(() => {
    let nextAccounts = accounts;

    if (filterUnownedAccounts)
      nextAccounts = nextAccounts.filter((account) => account.owner !== false);

    if (filterTerminatedAccounts)
      nextAccounts = nextAccounts.filter(
        (account) => account.status !== AccountStatus.terminated
      );

    if (filterSuspendedAccounts)
      nextAccounts = nextAccounts.filter(
        (account) => account.status !== AccountStatus.suspended
      );

    if (filterCryptoAccounts)
      nextAccounts = nextAccounts.filter(
        (account) => !isCryptoAccount(account)
      );

    return nextAccounts;
  }, [
    accounts,
    filterCryptoAccounts,
    filterSuspendedAccounts,
    filterTerminatedAccounts,
    filterUnownedAccounts
  ]);

  const findAccountByAccountNumber: FindAccountByAccountNumber = useCallback(
    (accountNumber: string | undefined) =>
      filteredAccounts.find(
        (account) => account.account_number === accountNumber
      ),
    [filteredAccounts]
  );

  return {
    accounts: filteredAccounts,
    isFetching,
    isSuccess,
    isLoading,
    findAccountByAccountNumber
  };
};
